import React from 'react'
import { ThemeProvider, Heading, Text, Button } from '@primer/react-brand'
import Container from './Container'
import { FIGURE_ICONS } from './landing/figureIcons'
import Eyebrow from './landing/Eyebrow'
import DisplayNumeral from './landing/DisplayNumeral'
import { FIGURES, FIGURES_AS_OF, PLATFORM_GROUPS, PLATFORM_COUNT, PENDING } from '../landingContent'

// The opening block is the one place the page goes dark. Primer Brand's own
// ThemeProvider flips every token inside it, so the Buttons, Headings and
// borders below need no special casing — only the section paints its own
// background, because the provider itself is transparent.

// Real content as the hero visual: the platform coverage, set as an instrument
// panel. It is the single most distinctive true fact about the product, and it
// is the fact the previous page buried in a row of pills two screens down.
const CoverageBoard = () => (
  <div
    aria-label={`Platform coverage: ${PLATFORM_COUNT} platforms`}
    style={{
      border: '1px solid var(--brand-color-border-default)',
      borderRadius: '12px',
      background: 'var(--brand-color-canvas-subtle)',
      padding: '20px 24px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: '18px',
    }}
  >
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        paddingBottom: '14px',
        borderBottom: '1px solid var(--brand-color-border-muted)',
      }}
    >
      <Eyebrow tone="muted">Coverage</Eyebrow>
      <Eyebrow tone="muted">{PLATFORM_COUNT} platforms</Eyebrow>
    </div>

    {PLATFORM_GROUPS.map(({ name, platforms }) => (
      <div key={name} className="coverage-row" style={{ display: 'grid', gridTemplateColumns: '104px 1fr', gap: '12px' }}>
        <Eyebrow tone="muted" as="h3" style={{ paddingTop: '4px' }}>{name}</Eyebrow>
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 8px',
          }}
        >
          {platforms.map((p) => (
            <li
              key={p}
              style={{
                fontFamily: 'var(--brand-fontStack-sansSerif)',
                fontSize: '14px',
                lineHeight: 1.4,
                fontWeight: 500,
                color: 'var(--brand-color-text-default)',
                padding: '3px 10px',
                borderRadius: '999px',
                border: '1px solid var(--brand-color-border-default)',
                background: 'var(--brand-color-canvas-default)',
                whiteSpace: 'nowrap',
              }}
            >
              {p}
            </li>
          ))}
        </ul>
      </div>
    ))}

    <div style={{ paddingTop: '14px', borderTop: '1px solid var(--brand-color-border-muted)' }}>
      <Eyebrow tone="muted">Speech transcribed · text in images extracted · all searchable</Eyebrow>
    </div>
  </div>
)

const Figures = () => (
  <div style={{ marginTop: '64px' }}>
    <dl
      className="figures-grid"
      style={{
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: '16px',
      }}
    >
      {FIGURES.map(({ value, label }, i) => {
        const Icon = FIGURE_ICONS[i]
        return (
          <div
            key={label}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              padding: '22px 22px',
              borderRadius: '12px',
              border: '1px solid var(--brand-color-border-default)',
              background: 'var(--brand-color-canvas-subtle)',
              minWidth: 0,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px',
                borderRadius: '11px',
                background: 'var(--base-color-scale-blue-5)',
                // Not text-onEmphasis: inside the dark ThemeProvider that token
                // flips dark, and the marks vanished into the badge.
                color: 'var(--base-color-scale-blue-0)',
                // Lets the overlapping avatar bubbles knock out the ones behind.
                '--badge-bg': 'var(--base-color-scale-blue-5)',
                flexShrink: 0,
              }}
            >
              <Icon />
            </span>
            <div style={{ minWidth: 0 }}>
              <dt style={{ margin: 0 }}>
                <DisplayNumeral size="figure" tone="accent">{value}</DisplayNumeral>
              </dt>
              <dd style={{ margin: '8px 0 0' }}>
                <Eyebrow tone="muted">{label}</Eyebrow>
              </dd>
            </div>
          </div>
        )
      })}
    </dl>
    <Text as="p" size="100" variant="subtle" style={{ margin: '20px 0 0' }}>
      Figures as of {FIGURES_AS_OF}.
    </Text>
  </div>
)

const HeroSection = ({ signUpPath = '/sign-up' }) => {
  return (
    <ThemeProvider colorMode="dark">
      <section
        style={{
          background: 'var(--brand-color-canvas-default)',
          color: 'var(--brand-color-text-default)',
          padding: '88px 0 64px',
          // Lighter accent step for legibility on the dark ground; see Eyebrow.
          '--cl-accent': 'var(--base-color-scale-blue-3)',
        }}
      >
        <Container>
          <div
            className="hero-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
              gap: '64px',
              alignItems: 'center',
            }}
          >
            <div>
              <Eyebrow>Public-interest social listening</Eyebrow>
              <Heading
                as="h1"
                size="1"
                weight="semibold"
                style={{ marginTop: '20px', textWrap: 'balance', maxWidth: '16ch', letterSpacing: '-0.02em' }}
              >
                Understand the conversation, not just your feed.
              </Heading>
              <Text as="p" size="400" variant="muted" style={{ marginTop: '24px', marginBottom: 0, maxWidth: '560px' }}>
                CivicLens collects posts from the accounts and topics you choose, across {PLATFORM_COUNT}{' '}
                platforms, and gives you the tools to search them, track them over time and see who is
                driving a conversation. Built for journalists, researchers and civil society — not for
                marketing budgets.
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '36px' }}>
                <Button as="a" href={signUpPath} variant="primary" size="large">
                  Start a 7-day trial
                </Button>
                <Button as="a" href="#how-it-works" variant="secondary" size="large">
                  See how it works
                </Button>
              </div>
              <Text as="p" size="100" variant="subtle" style={{ marginTop: '16px', marginBottom: 0 }}>
                Full Advanced toolset for a week. Nothing charged until the trial ends.
              </Text>
            </div>

            {/* When a product visual is approved (PENDING.heroVisual) it goes here and
                the coverage board moves down into the Platforms section. */}
            {!PENDING.heroVisual && <CoverageBoard />}
          </div>

          <Figures />
        </Container>
      </section>
    </ThemeProvider>
  )
}

export default HeroSection
